<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    :permanent="!isMobile"
    :temporary="isMobile"
    rail-width="72"
    width="324"
    :rail="isMobile ? false : rail"
  >
    <div :class="`${rail || isMobile ? 'px-4' : 'px-6'} py-4 sidebar`">
      <!-- Mobile Sidebar Header -->
      <div v-if="isMobile" class="mobile-sidebar-header">
        <v-img
          :src="ottuLogo"
          alt="Ottu Logo"
          class="sidebar-logo"
          @click="
            () => {
              drawer = false;
              $emit(`go-home`);
            }
          "
        />
        <!-- same logic as the logo click on desktop screen, just emit go-home and close the drawer -->
        <v-btn
          icon
          size="40"
          variant="text"
          :ripple="false"
          class="menu-icon"
          aria-label="Close navigation menu"
          @click="drawer = false"
        >
          <v-icon :size="24" aria-hidden="true">mdi-backburger</v-icon>
        </v-btn>
      </div>

      <!-- Desktop Sidebar Header with Menu Toggle -->
      <div v-if="!isMobile" class="d-flex align-center">
        <v-btn
          icon
          size="40"
          :ripple="false"
          class="ml-auto menu-icon"
          :aria-label="rail ? 'Expand sidebar' : 'Collapse sidebar'"
          @click="rail = !rail"
        >
          <v-icon :size="24" aria-hidden="true">{{
            rail ? 'mdi-menu' : 'mdi-backburger'
          }}</v-icon>
        </v-btn>
      </div>

      <!-- Agent Selector Expandable -->
      <v-fade-transition>
        <div
          v-if="isMobile || !rail"
          class="agent-selector"
          v-click-outside="handleOutsideClick"
          @click="expanded = !expanded"
        >
          <v-btn variant="text" class="agent-selector-btn">
            <template v-slot:prepend>
              <v-icon :size="24" class="text-white"
                >mdi-comment-edit-outline</v-icon
              >
            </template>
            <span class="new-chat-text">{{ 'New Chat' }}</span>
            <template v-slot:append>
              <v-icon :size="24" class="text-white">
                {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </template>
          </v-btn>

          <v-expand-transition>
            <div v-show="expanded">
              <v-list density="compact" class="pa-0">
                <v-list-item
                  v-for="agent in agents"
                  :key="agent.name"
                  class="agent-item"
                  @click="$emit('new-chat', agent)"
                >
                  <v-list-item-title class="agent-item-title">
                    {{ agent.name }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </div>
          </v-expand-transition>
        </div>
      </v-fade-transition>

      <!-- Search Chats Button -->
      <v-fade-transition>
        <div
          v-if="isMobile || !rail"
          class="search-chats-btn"
          @click="$emit('open-search')"
        >
          <v-icon :size="24" aria-hidden="true">mdi-magnify</v-icon>
          <span class="search-chats-text">Search Chats</span>
        </div>
      </v-fade-transition>

      <!-- History Section -->
      <v-fade-transition>
        <div v-if="isMobile || !rail" class="history-section">
          <div class="history-header">
            <div class="history-title">History</div>
            <div v-if="activeFilterAgent" class="agent-filter-chip">
              <span class="agent-filter-bracket">[</span>
              <span class="agent-filter-name">{{
                activeFilterAgent.name
              }}</span>
              <v-icon
                size="16"
                class="agent-filter-close"
                aria-label="Clear agent filter"
                @click="conversationStore.setAgentFilter(null)"
                >mdi-close-circle-outline</v-icon
              >
              <span class="agent-filter-bracket">]</span>
            </div>
          </div>

          <div class="conversation-list">
            <!-- Loading State (Initial) -->
            <v-skeleton-loader
              v-if="conversationStore.isLoading && conversations.length === 0"
              :type="`list-item@${conversationStore.limit}`"
            ></v-skeleton-loader>

            <!-- Empty State -->
            <div v-else-if="conversations.length === 0" class="empty-history">
              <v-icon size="32" class="empty-history-icon"
                >mdi-chat-outline</v-icon
              >
              <p class="empty-history-title">No chats yet</p>
              <p class="empty-history-subtitle">
                Start a new chat to begin a conversation
              </p>
            </div>

            <!-- Conversation List -->
            <v-list v-else density="compact" class="pa-0">
              <v-list-item
                v-for="conversation in conversations"
                :key="conversation.id"
                :active="
                  route.name === 'conversation' &&
                  route.params.id === conversation.id
                "
                class="rounded mb-3 pa-3"
                @click="
                  route.params.id !== conversation.id &&
                    $emit('select-conversation', conversation)
                "
              >
                <v-list-item-title class="conversation-title">
                  {{ conversation.title || conversation.id }}
                </v-list-item-title>
                <template #append>
                  <div class="conversation-actions">
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      color="white"
                      aria-label="Rename conversation"
                      @click.stop="openRenameDialog(conversation)"
                    >
                      <v-icon :size="18" aria-hidden="true"
                        >mdi-rename-box-outline</v-icon
                      >
                    </v-btn>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      color="white"
                      aria-label="Delete conversation"
                      @click.stop="openDeleteDialog(conversation)"
                    >
                      <v-icon :size="18" aria-hidden="true"
                        >mdi-delete-outline</v-icon
                      >
                    </v-btn>
                  </div>
                </template>
              </v-list-item>

              <!-- Intersection Observer Target -->
              <div v-intersect="onIntersect" class="load-more-trigger"></div>

              <!-- Loading More State -->
              <v-skeleton-loader
                v-if="conversationStore.isLoading && conversations.length > 0"
                type="list-item@3"
              ></v-skeleton-loader>
            </v-list>
          </div>
        </div>
      </v-fade-transition>
    </div>
  </v-navigation-drawer>

  <RenameConversationDialog
    v-model="showRenameDialog"
    :conversation="conversationToRename"
  />

  <DeleteConversationDialog
    v-model="showDeleteDialog"
    :conversation="conversationToDelete"
  />
</template>

<script setup lang="ts">
import ottuLogo from '@/assets/ottu-logo.svg';
import DeleteConversationDialog from '@/components/DeleteConversationDialog.vue';
import RenameConversationDialog from '@/components/RenameConversationDialog.vue';
import { Agent } from '@/services/agentService';
import { type Conversation } from '@/services/conversationService';
import { useAgentStore } from '@/stores/agentStore';
import { useConversationStore } from '@/stores/conversationStore';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps<{
  drawer: boolean;
  rail: boolean;
  isMobile: boolean;
  agents: Agent[];
  conversations: Conversation[];
}>();

const route = useRoute();
const agentStore = useAgentStore();
const conversationStore = useConversationStore();

const emit = defineEmits<{
  'update:drawer': [value: boolean];
  'update:rail': [value: boolean];
  'new-chat': [agent: Agent];
  'select-conversation': [conversation: Conversation];
  'open-search': [];
  'go-home': [];
}>();

const expanded = ref(false);

const activeFilterAgent = computed(() =>
  conversationStore.activeAgentFilter
    ? agentStore.getAgentById(conversationStore.activeAgentFilter)
    : null,
);
const showDeleteDialog = ref(false);
const conversationToDelete = ref<Conversation | null>(null);
const showRenameDialog = ref(false);
const conversationToRename = ref<Conversation | null>(null);

const drawer = computed({
  get: () => props.drawer,
  set: (value) => emit('update:drawer', value),
});

const rail = computed({
  get: () => props.rail,
  set: (value) => emit('update:rail', value),
});

const handleOutsideClick = () => {
  if (expanded.value) {
    expanded.value = false;
  }
};

const openRenameDialog = (conversation: Conversation) => {
  conversationToRename.value = conversation;
  showRenameDialog.value = true;
};

const openDeleteDialog = (conversation: Conversation) => {
  conversationToDelete.value = conversation;
  showDeleteDialog.value = true;
};

const onIntersect = (isIntersecting: boolean) => {
  if (
    isIntersecting &&
    conversationStore.hasMore &&
    !conversationStore.isLoading
  ) {
    conversationStore.loadMoreConversations();
  }
};
</script>

<style lang="scss" scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.3s ease;
}

.menu-icon {
  background-color: rgb(var(--v-theme-background));
  border-radius: 8px;
}

.mobile-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.sidebar-logo {
  width: 70px;
  height: 24px;
}

.agent-selector {
  margin-top: 60px;

  @media only screen and (width < $breakpoint-mobile) {
    margin-top: 16px;
  }
  margin-bottom: 16px;
  color: #959595;
  background-color: rgb(var(--v-theme-background));
  border-radius: 8px;
  cursor: pointer;
}

.agent-selector-btn {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: 48px !important;
  padding: 12px 16px;
}

.agent-selector-btn ::v-deep(.v-btn__append) {
  margin-left: auto;
}

.new-chat-text {
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: none;
  letter-spacing: normal;
  text-indent: 0%;
}

.agent-item {
  padding: 16px;
  transition: background-color 0.2s, color 0.2s;
  cursor: pointer;
}

.agent-item:hover {
  .agent-item-title {
    color: #f77d1a !important;
  }

  background-color: rgb(var(--v-theme-surface)) !important;
}

.agent-item:hover::v-deep(.v-list-item__overlay) {
  opacity: 0 !important;
}

.agent-item-title {
  padding-left: 54px;
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 100% */
}

.search-chats-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  min-width: 244px;
  margin-bottom: 32px;
  padding: 12px 16px;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: color 0.2s;
}

.search-chats-btn:hover {
  color: #f77d1a;
}

.search-chats-text {
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.history-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.history-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}

.history-title {
  color: #959595;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.agent-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 4px 16px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  cursor: default;
}

.agent-filter-bracket,
.agent-filter-name {
  color: #d9d9d9;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.agent-filter-close {
  color: #d9d9d9;
  cursor: pointer;
}

.conversation-list {
  overflow-y: auto;

  .v-list-item {
    border-radius: 12px !important;
  }

  .v-list-item--active {
    background-color: #f77d1a !important;
  }
}

.conversation-title {
  color: #fff;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
}

.empty-history {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  text-align: center;
}

.empty-history-icon {
  color: #959595;
  margin-bottom: 4px;
}

.empty-history-title {
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  margin: 0;
}

.empty-history-subtitle {
  color: #959595;
  font-family: Poppins;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  margin: 0;
}

.conversation-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.load-more-trigger {
  height: 1px;
  visibility: hidden;
}
</style>
